import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { type OportunidadeConsolidada, formatBRL, formatPercent } from '@/pages/painel/lib/comercial';

export default function NegociosQuentes({ password }: { password: string }) {
  const [ops, setOps] = useState<OportunidadeConsolidada[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'listar', password, filtros: { estado: 'abertas' } },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { oportunidades?: OportunidadeConsolidada[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);

      const lista = (parsed?.oportunidades ?? []).slice().sort((a, b) => {
        const la = a.lucro_ponderado ?? 0;
        const lb = b.lucro_ponderado ?? 0;
        if (lb !== la) return lb - la;
        if ((b.valor ?? 0) !== (a.valor ?? 0)) return (b.valor ?? 0) - (a.valor ?? 0);
        return (b.probabilidade ?? 0) - (a.probabilidade ?? 0);
      });

      setOps(lista.slice(0, 5));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar negócios quentes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#E8610A]/10 text-[#E8610A]">
            <i className="ri-fire-line"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Negócios quentes</h2>
            <p className="text-xs text-gray-500">Melhores oportunidades abertas, por lucro ponderado, valor e probabilidade.</p>
          </div>
        </div>
        <Link to="/painel/oportunidades" className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] whitespace-nowrap">
          Ver todas <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">{error}</div>}

      {loading && ops.length === 0 ? (
        <div className="flex justify-center py-8"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : ops.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">Nenhuma oportunidade aberta no momento.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {ops.map((o) => (
            <Link
              key={o.id}
              to="/painel/oportunidades"
              className="group bg-gray-50 rounded-xl border border-gray-100 p-4 hover:border-[#E8610A]/40 transition-colors cursor-pointer"
            >
              <p className="text-xs text-gray-500 truncate font-medium">{o.empresa ?? 'Sem empresa'}</p>
              <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mt-0.5 group-hover:text-[#E8610A] transition-colors">
                {o.titulo}
              </p>

              <div className="mt-3 flex items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Valor</p>
                  <p className="text-sm font-bold text-gray-900">{formatBRL(o.valor)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Lucro</p>
                  <p className="text-sm font-bold text-green-700">{formatBRL(o.lucro)}</p>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {o.margem != null && (
                  <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${
                    o.margem_tone === 'verde' ? 'bg-green-50 text-green-700 border-green-200'
                    : o.margem_tone === 'amarela' ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-red-50 text-red-600 border-red-200'
                  }`}>
                    {formatPercent(o.margem)}
                  </span>
                )}
                {o.probabilidade != null && (
                  <span className="text-[11px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                    {formatPercent(o.probabilidade)}
                  </span>
                )}
              </div>

              <div className="mt-2 text-[11px] text-gray-500">
                <span className="text-gray-400">Ponderado</span>{' '}
                <span className="font-semibold text-gray-700">{formatBRL(o.lucro_ponderado)}</span>
              </div>

              {o.proxima_acao ? (
                <p className="mt-2 text-[11px] text-gray-500 truncate" title={o.proxima_acao}>
                  <i className="ri-arrow-right-circle-line text-[#E8610A]"></i> {o.proxima_acao}
                </p>
              ) : (
                <p className="mt-2 text-[11px] font-semibold text-red-600 inline-flex items-center gap-1">
                  <i className="ri-alert-line"></i> Sem próxima ação
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}